import { isAxiosError } from 'axios';
import { apiClient } from './apiClient';
import {
  Service,
  ServiceFilters,
  ServiceSortOption,
  ServiceCategory,
} from '../types/service.types';

/**
 * Apply sorting to services (client-side)
 * Backend returns ordered by displayOrder, but we may need custom sorts
 */
function sortServices(services: Service[], sortBy: ServiceSortOption): Service[] {
  const sorted = [...services];

  switch (sortBy) {
    case 'price-asc':
      sorted.sort((a, b) => a.basePrice - b.basePrice);
      break;
    case 'price-desc':
      sorted.sort((a, b) => b.basePrice - a.basePrice);
      break;
    case 'duration-asc':
      sorted.sort((a, b) => a.duration - b.duration);
      break;
    case 'duration-desc':
      sorted.sort((a, b) => b.duration - a.duration);
      break;
    case 'name-asc':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'popular':
    default:
      // Backend already returns by displayOrder
      break;
  }

  return sorted;
}

/**
 * Apply client-side filters for search, price, and duration
 * Category filtering is done server-side
 */
function applyClientFilters(services: Service[], filters: ServiceFilters): Service[] {
  let filtered = [...services];

  // Apply search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(
      (s) =>
        s.name.toLowerCase().includes(searchLower) ||
        s.description.toLowerCase().includes(searchLower)
    );
  }

  // Apply price filters
  if (filters.minPrice !== undefined) {
    filtered = filtered.filter((s) => s.basePrice >= filters.minPrice!);
  }
  if (filters.maxPrice !== undefined) {
    filtered = filtered.filter((s) => s.basePrice <= filters.maxPrice!);
  }

  // Apply duration filters
  if (filters.minDuration !== undefined) {
    filtered = filtered.filter((s) => s.duration >= filters.minDuration!);
  }
  if (filters.maxDuration !== undefined) {
    filtered = filtered.filter((s) => s.duration <= filters.maxDuration!);
  }

  return filtered;
}

export const servicesApi = {
  /**
   * Get all services with optional filters and sorting
   */
  async getAll(filters: ServiceFilters, sortBy: ServiceSortOption): Promise<Service[]> {
    const params = new URLSearchParams();

    // Only send category to server if specified and not 'all'
    if (filters.category && filters.category !== 'all') {
      params.append('category', filters.category);
    }

    const url = params.toString()
      ? `/api/services?${params.toString()}`
      : '/api/services';

    const response = await apiClient.get<{ success: boolean; data: { services: Service[]; total: number } }>(
      url
    );

    // Apply client-side filters (search, price, duration)
    let services = applyClientFilters(response.data.data.services, filters);

    // Apply sorting
    services = sortServices(services, sortBy);

    return services;
  },

  /**
   * Get a single service by slug
   */
  async getBySlug(slug: string): Promise<Service | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: Service }>(
        `/api/services/by-slug/${slug}`
      );
      return response.data.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get a single service by ID
   */
  async getById(id: string): Promise<Service | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: Service }>(
        `/api/services/${id}`
      );
      return response.data.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get all service categories
   */
  async getCategories(): Promise<ServiceCategory[]> {
    // Categories are static for now
    return ['Relaxation', 'Therapeutic', 'Sports', 'Specialized'];
  },
};
